class ProductPurchaseBarController {
  constructor(productInfo) {
    this.productInfo = productInfo;
    this.sectionId = productInfo.dataset.section;
    this.stickyBar = productInfo.querySelector(`#ProductPurchaseSticky-${this.sectionId}`);
    this.tastesSection = productInfo.querySelector(`#ProductTastes-${this.sectionId}`);
    this.variantSelectors = productInfo.querySelector('variant-selects');
    this.priceRoot = productInfo.querySelector(`#price-${this.sectionId}`);
    this.productForm = productInfo.querySelector(`#product-form-${this.sectionId}`);
    this.stickyPrice = this.stickyBar?.querySelector('[data-sticky-price]');
    this.stickySummary = this.stickyBar?.querySelector('[data-sticky-summary]');
    this.stickySubmit = this.stickyBar?.querySelector('[data-sticky-submit]');
    this.heroSubmit = productInfo.querySelector('.product-purchase__hero-atc');
    this.mainQuantityInput = productInfo.querySelector(`#Quantity-${this.sectionId}`);
    this.stickyQuantity = null;
    this.stickyQuantityInput = null;
    this.lastQuantity = this.mainQuantityInput?.value || '1';
    this.tastesObserver = null;
    this.addToCartObserver = null;
    this.footerObserver = null;
    this.variantChangeUnsubscriber = null;
    this.priceObserver = null;
    this.formObserver = null;
    this.hasReachedTastes = false;
    this.visibleAddToCartButtons = new Set();
    this.visibleFooters = new Set();
    this.normalAddToCartButtons = [];
    this.footerElements = [];
    this.stickyOriginalParent = this.stickyBar?.parentNode;
    this.stickyPlaceholder = document.createComment(`ProductPurchaseSticky-${this.sectionId}`);
    this.onChange = this.sync.bind(this);
    this.onResize = this.handleViewportChange.bind(this);
    if (!this.stickyBar || !this.tastesSection || !this.productForm) return;
    this.init();
  }

  init() {
    this.portalStickyBar();
    this.createStickyQuantityControl();
    this.variantSelectors?.addEventListener('change', this.onChange);
    this.productForm.addEventListener('change', this.onChange);
    this.mainQuantityInput?.addEventListener('change', this.onChange);
    this.mainQuantityInput?.addEventListener('input', this.onChange);
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
      this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        if (event?.data?.sectionId !== this.sectionId) return;
        this.restoreQuantity();
      });
    }
    window.addEventListener('resize', this.onResize, { passive: true });

    this.priceObserver = new MutationObserver(() => this.syncPrice());
    if (this.priceRoot) {
      this.priceObserver.observe(this.priceRoot, { childList: true, subtree: true, characterData: true });
    }

    this.formObserver = new MutationObserver(() => this.syncButtonState());
    const submitButton = this.productForm.querySelector(`#ProductSubmitButton-${this.sectionId}`);
    if (submitButton) {
      this.formObserver.observe(submitButton, {
        attributes: true,
        childList: true,
        subtree: true,
        characterData: true,
      });
    }

    this.tastesObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          this.hasReachedTastes = entry.isIntersecting || entry.boundingClientRect.top <= 0;
        });
        this.updateStickyVisibility();
      },
      {
        threshold: 0,
      }
    );

    this.addToCartObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.visibleAddToCartButtons.add(entry.target);
          } else {
            this.visibleAddToCartButtons.delete(entry.target);
          }
        });
        this.updateStickyVisibility();
      },
      {
        threshold: 0,
      }
    );

    this.footerObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.visibleFooters.add(entry.target);
          } else {
            this.visibleFooters.delete(entry.target);
          }
        });
        this.updateStickyVisibility();
      },
      {
        threshold: 0,
      }
    );

    this.normalAddToCartButtons = this.getNormalAddToCartButtons();
    this.footerElements = this.getFooters();
    this.refreshObservedVisibility();
    this.tastesObserver.observe(this.tastesSection);
    this.normalAddToCartButtons.forEach((button) => this.addToCartObserver.observe(button));
    this.footerElements.forEach((footer) => this.footerObserver.observe(footer));
    this.sync();
    this.updateStickyVisibility();
  }

  isMobileViewport() {
    return window.matchMedia('(max-width: 768px)').matches;
  }

  handleViewportChange() {
    this.portalStickyBar();
    this.sync();
    this.refreshObservedVisibility();
    this.updateStickyVisibility();
  }

  portalStickyBar() {
    if (!this.stickyBar || this.stickyBar.parentNode === document.body) return;

    this.stickyOriginalParent = this.stickyBar.parentNode;
    this.stickyOriginalParent.insertBefore(this.stickyPlaceholder, this.stickyBar);
    document.body.appendChild(this.stickyBar);
  }

  createStickyQuantityControl() {
    if (!this.mainQuantityInput || this.stickyQuantity || !this.stickySubmit) return;

    this.stickyQuantity = document.createElement('div');
    this.stickyQuantity.className = 'product-purchase-sticky__quantity';
    this.stickyQuantity.innerHTML = `
      <button class="product-purchase-sticky__quantity-button" name="minus" type="button" aria-label="Decrease quantity">-</button>
      <input class="product-purchase-sticky__quantity-input" type="number" inputmode="numeric" aria-label="Quantity">
      <button class="product-purchase-sticky__quantity-button" name="plus" type="button" aria-label="Increase quantity">+</button>
    `;

    this.stickyQuantityInput = this.stickyQuantity.querySelector('input');
    this.stickySubmit.insertAdjacentElement('beforebegin', this.stickyQuantity);

    this.stickyQuantity.querySelectorAll('button').forEach((button) => {
      button.addEventListener('click', () => this.adjustQuantity(button.name));
    });

    this.stickyQuantityInput.addEventListener('input', () => this.setMainQuantity(this.stickyQuantityInput.value));
    this.stickyQuantityInput.addEventListener('change', () => this.setMainQuantity(this.stickyQuantityInput.value));
    this.syncQuantity();
  }

  isElementVisibleInViewport(element) {
    if (!element || element.getClientRects().length === 0) return false;

    const rect = element.getBoundingClientRect();
    return rect.bottom > 0 && rect.right > 0 && rect.top < window.innerHeight && rect.left < window.innerWidth;
  }

  refreshObservedVisibility() {
    this.hasReachedTastes =
      this.isElementVisibleInViewport(this.tastesSection) || this.tastesSection.getBoundingClientRect().top <= 0;

    this.visibleAddToCartButtons.clear();
    this.normalAddToCartButtons.forEach((button) => {
      if (this.isElementVisibleInViewport(button)) this.visibleAddToCartButtons.add(button);
    });

    this.visibleFooters.clear();
    this.footerElements.forEach((footer) => {
      if (this.isElementVisibleInViewport(footer)) this.visibleFooters.add(footer);
    });
  }

  getNormalAddToCartButtons() {
    const selectors = [
      `button[type="submit"][form="${this.productForm.id}"]`,
      `button[name="add"][form="${this.productForm.id}"]`,
      'button[name="add"]',
      '.product-form__submit',
    ];

    return Array.from(this.productInfo.querySelectorAll(selectors.join(','))).filter(
      (button) => button !== this.stickySubmit && !button.hasAttribute('data-sticky-submit')
    );
  }

  getFooters() {
    return Array.from(
      document.querySelectorAll('footer, .footer, [id^="shopify-section-footer"], .shopify-section-group-footer-group')
    );
  }

  updateStickyVisibility() {
    const shouldShow =
      this.hasReachedTastes &&
      this.visibleAddToCartButtons.size === 0 &&
      this.visibleFooters.size === 0;

    this.toggleSticky(shouldShow);
  }

  toggleSticky(shouldShow) {
    this.stickyBar.hidden = !shouldShow;
    this.stickyBar.classList.toggle('is-visible', shouldShow);
  }

  sync() {
    this.syncPrice();
    this.syncSummary();
    this.syncQuantity();
    this.syncButtonState();
  }

  adjustQuantity(direction) {
    if (!this.mainQuantityInput) return;

    const previousValue = this.mainQuantityInput.value;
    if (direction === 'plus') {
      this.mainQuantityInput.stepUp();
    } else {
      this.mainQuantityInput.stepDown();
    }

    if (this.mainQuantityInput.value !== previousValue) {
      this.lastQuantity = this.mainQuantityInput.value;
      this.mainQuantityInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    this.syncQuantity();
  }

  setMainQuantity(value) {
    if (!this.mainQuantityInput || value === '') return;

    this.mainQuantityInput.value = this.normalizeQuantity(value);
    this.lastQuantity = this.mainQuantityInput.value;
    this.mainQuantityInput.dispatchEvent(new Event('change', { bubbles: true }));
    this.syncQuantity();
  }

  restoreQuantity() {
    if (!this.mainQuantityInput || !this.lastQuantity) return;

    this.mainQuantityInput.value = this.normalizeQuantity(this.lastQuantity);
    this.mainQuantityInput.dispatchEvent(new Event('change', { bubbles: true }));
    this.sync();
  }

  normalizeQuantity(value) {
    const min = Number(this.mainQuantityInput.getAttribute('min') || this.mainQuantityInput.dataset.min || 1);
    const maxValue = this.mainQuantityInput.getAttribute('max') || this.mainQuantityInput.dataset.max;
    const max = maxValue ? Number(maxValue) : null;
    const step = Number(this.mainQuantityInput.getAttribute('step') || 1);
    let nextValue = Number(value);

    if (!Number.isFinite(nextValue)) nextValue = min;
    nextValue = Math.max(nextValue, min);
    if (max !== null && Number.isFinite(max)) nextValue = Math.min(nextValue, max);

    const offset = nextValue - min;
    if (step > 1 && offset % step !== 0) {
      nextValue = min + Math.round(offset / step) * step;
      if (max !== null && Number.isFinite(max)) nextValue = Math.min(nextValue, max);
    }

    return String(nextValue);
  }

  syncQuantity() {
    if (!this.mainQuantityInput || !this.stickyQuantityInput) return;

    if (this.mainQuantityInput.value) this.lastQuantity = this.mainQuantityInput.value;

    ['min', 'max', 'step'].forEach((attribute) => {
      const value = this.mainQuantityInput.getAttribute(attribute);
      if (value === null) {
        this.stickyQuantityInput.removeAttribute(attribute);
      } else {
        this.stickyQuantityInput.setAttribute(attribute, value);
      }
    });

    this.stickyQuantityInput.value = this.mainQuantityInput.value || this.lastQuantity;
  }

  syncPrice() {
    if (!this.stickyPrice || !this.priceRoot) return;
    const priceTarget =
      this.priceRoot.querySelector('.price-item--sale') ||
      this.priceRoot.querySelector('.price-item--regular') ||
      this.priceRoot;
    const nextPrice = priceTarget.textContent.replace(/\s+/g, ' ').trim();
    if (nextPrice) this.stickyPrice.textContent = nextPrice;
  }

  syncSummary() {
    if (!this.stickySummary || !this.variantSelectors) return;

    const values = Array.from(this.variantSelectors.querySelectorAll('.product-form__input'))
      .map((group) => {
        const select = group.querySelector('select');
        if (select) {
          const selectedOption = select.selectedOptions?.[0];
          return selectedOption?.dataset.optionLabel || selectedOption?.textContent;
        }

        const checked = group.querySelector('input[type="radio"]:checked');
        if (!checked) return '';

        return checked.dataset.optionLabel || checked.value;
      })
      .map((value) => value?.replace(/\s+/g, ' ')?.trim());

    const summary = values.filter(Boolean).join(' • ');
    if (summary) this.stickySummary.textContent = summary;
  }

  syncButtonState() {
    const sourceButton = this.productForm.querySelector(`#ProductSubmitButton-${this.sectionId}`);
    if (!sourceButton) return;

    const label = sourceButton.textContent.replace(/\s+/g, ' ').trim();
    const isDisabled = sourceButton.hasAttribute('disabled') || sourceButton.getAttribute('aria-disabled') === 'true';

    [this.stickySubmit, this.heroSubmit].forEach((button) => {
      if (!button) return;
      button.disabled = isDisabled;
      if (label) button.textContent = label;
    });
  }

  disconnect() {
    this.variantSelectors?.removeEventListener('change', this.onChange);
    this.productForm?.removeEventListener('change', this.onChange);
    this.mainQuantityInput?.removeEventListener('change', this.onChange);
    this.mainQuantityInput?.removeEventListener('input', this.onChange);
    this.variantChangeUnsubscriber?.();
    window.removeEventListener('resize', this.onResize);
    this.tastesObserver?.disconnect();
    this.addToCartObserver?.disconnect();
    this.footerObserver?.disconnect();
    this.priceObserver?.disconnect();
    this.formObserver?.disconnect();
    if (this.stickyPlaceholder?.parentNode && this.stickyBar) {
      this.stickyPlaceholder.parentNode.insertBefore(this.stickyBar, this.stickyPlaceholder);
      this.stickyPlaceholder.remove();
    }
  }
}

const purchaseBarControllers = new WeakMap();

function initProductPurchaseBar(productInfo) {
  if (!productInfo || purchaseBarControllers.has(productInfo)) return;
  const controller = new ProductPurchaseBarController(productInfo);
  purchaseBarControllers.set(productInfo, controller);
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll("product-info[id^='MainProduct-']").forEach(initProductPurchaseBar);
});

document.addEventListener('product-info:loaded', (event) => {
  initProductPurchaseBar(event.target.closest("product-info[id^='MainProduct-']") || event.target);
});
